import { AxleButton } from '@/components/buttons/AxleButton';
import { APPOINTMENT_CANCELLATION_REASONS } from '@/components/cancel/Cancel.operations';
import {
    AppointmentCancellationReasonsQuery,
    AppointmentCancellationReasonsQueryVariables,
} from '@/components/cancel/Cancel.operations.gql.types';
import { AxleCallout } from '@/components/displayFields/AxleCallout';
import { AxleSelectField } from '@/components/formFields/AxleSelectField';
import { AxleCheckboxField } from '@/components/formFields/checkboxes';
import { AxleConfirmDialog } from '@/components/layout/AxleConfirmDialog';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { useCancelVisit } from '@/fetching/api/visits';
import { errorToast } from '@/toast';
import { useBooleanState } from '@/utils/booleanState';
import { useInputState, useJointInputState } from '@/utils/inputState';
import { useApolloClient, useQuery } from '@apollo/client';
import { FC, useCallback, useMemo } from 'react';

type CancelVisitDialogProps = {
    isOpen: boolean;
    onClose(): void;
    visitId: string;
    patientId: string;
};

export const CancelVisitDialog: FC<CancelVisitDialogProps> = (props) => {
    const { isOpen, onClose, visitId, patientId } = props;
    const client = useApolloClient();
    const { data, loading } = useQuery<
        AppointmentCancellationReasonsQuery,
        AppointmentCancellationReasonsQueryVariables
    >(APPOINTMENT_CANCELLATION_REASONS, {
        skip: !isOpen,
    });

    const cancellationReasons = useMemo(() => {
        if (!data?.optionValueSet?.values) {
            return [];
        }
        return [...data.optionValueSet.values].sort((a, b) => a.sortOrder - b.sortOrder);
    }, [data]);

    const requiresCancellationReason = cancellationReasons.length > 0 || loading;

    const shouldSendPatientCancelAlert = useInputState<boolean>(true);
    const cancellationReason = useInputState<string>('', {
        required: requiresCancellationReason,
    });
    const { checkValid } = useJointInputState([shouldSendPatientCancelAlert, cancellationReason]);

    const cancelVisit = useCancelVisit({
        visitId,
        patientId,
    });

    const onConfirm = useCallback(() => {
        if (!checkValid()) {
            return;
        }
        void cancelVisit({
            should_send_patient_alerts: shouldSendPatientCancelAlert.value,
            cancellation_reason_uuid:
                cancellationReason.value.length === 0 ? null : cancellationReason.value,
        }).then(() => {
            client.cache.evict({ fieldName: 'visits' });
            client.refetchQueries({ include: 'active' }).catch(() => {
                errorToast('Failed to refresh visits.');
            });
            onClose();
        });
    }, [
        cancelVisit,
        cancellationReason.value,
        checkValid,
        client,
        onClose,
        shouldSendPatientCancelAlert.value,
    ]);

    return (
        <AxleConfirmDialog
            isOpen={isOpen}
            close={onClose}
            confirmCta={'Confirm'}
            cancelCta={'Back'}
            onConfirm={onConfirm}
            title='Cancel Visit?'
        >
            Are you sure you want to cancel this visit?
            <AxleCallout type='warning'>This will cancel the visit in the system.</AxleCallout>
            <VerticalStack spacing={2}>
                {cancellationReasons.length > 0 && (
                    <AxleSelectField
                        label='Cancellation Reason'
                        inputState={cancellationReason}
                        options={cancellationReasons}
                        optionToValue={(reason) => reason.id}
                        optionToLabel={(reason) => reason.name}
                    />
                )}
                <AxleCheckboxField
                    checkboxText='Notify patient of cancellation?'
                    inputState={shouldSendPatientCancelAlert}
                />
            </VerticalStack>
        </AxleConfirmDialog>
    );
};

type CancelButtonProps = {
    visitId: string;
    patientId: string;
};

export const CancelVisitButton: FC<CancelButtonProps> = (props) => {
    const { visitId, patientId } = props;
    const [
        isCancelConfirmationDialogOpen,
        openCancelConfirmationDialog,
        closeCancelConfirmationDialog,
    ] = useBooleanState(false);

    return (
        <>
            <CancelVisitDialog
                isOpen={isCancelConfirmationDialogOpen}
                onClose={closeCancelConfirmationDialog}
                visitId={visitId}
                patientId={patientId}
            />
            <AxleButton onClick={openCancelConfirmationDialog}>Cancel</AxleButton>
        </>
    );
};
